import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { setRememberedAccessGuid } from '../utils/rememberedAccessGuid';
import { registerSessionExpiryHandler } from '../utils/sessionExpiry';

/**
 * On session expiry, clear the cookie session but keep a recovery path:
 * navigate home with resume GUID/email in location state (RSW-398).
 * Soft-persist the GUID so "Continue" survives dismiss/reload; explicit
 * logout still clears it via AuthContext.logout().
 */
export default function SessionBridge() {
  const navigate = useNavigate();
  const { clearClientSession, verifyCurrentSession, reconcileVerifiedSession, userType, user } = useAuth();
  const userTypeRef = useRef(userType);
  const userRef = useRef(user);
  const verifyRef = useRef(verifyCurrentSession);
  const reconcileRef = useRef(reconcileVerifiedSession);
  userTypeRef.current = userType;
  userRef.current = user;
  verifyRef.current = verifyCurrentSession;
  reconcileRef.current = reconcileVerifiedSession;

  useEffect(() => {
    registerSessionExpiryHandler(() => {
      // A 401 can come from a request started before another tab refreshed the
      // shared cookie. Tearing down on it would clear that fresh account, so
      // probe the CURRENT session first — read-only, so it cannot strand an
      // in-flight login check — and only a confirmed-unauthenticated result
      // may proceed to expiry recovery.
      void (async () => {
        let verified;
        try {
          verified = await verifyRef.current?.();
        } catch {
          return;
        }
        if (!verified || verified.stale || verified.transient) return;
        if (verified.authenticated) {
          // The probe may have verified a different account than the one on
          // screen (its cross-tab signal was lost) — reconcile the verified
          // identity rather than leaving the stale view up.
          reconcileRef.current?.(verified);
          return;
        }

        const audience = userTypeRef.current;
        const current = userRef.current;
        const resumeGuid = typeof current?.guid === 'string' ? current.guid.trim() : '';
        const resumeEmail = typeof current?.email === 'string' ? current.email.trim() : '';

        if (audience === 'JOBSEEKER' && resumeGuid) {
          setRememberedAccessGuid(resumeGuid);
        }

        clearClientSession();
        navigate('/', {
          replace: true,
          state: {
            sessionExpired: true,
            audience: audience || null,
            resumeGuid: resumeGuid || null,
            resumeEmail: resumeEmail || null,
          },
        });
      })();
    });
    return () => registerSessionExpiryHandler(null);
  }, [navigate, clearClientSession]);

  return null;
}
